// BilletterieStatsModal.jsx — Phase D : stats détaillées billetterie par zone+tarif
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { X, Loader2, BarChart3, Tag, Theater, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const COLOR_HEX = {
  gold: '#f5a623', purple: '#9b59b6', blue: '#4f8aff', green: '#22d3a0', cyan: '#4DFFD2',
};

export default function BilletterieStatsModal({ evenementId, onClose }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAttribution, setShowAttribution] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/evenements/${evenementId}/billetterie-stats`);
      setStats(data);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur de chargement');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evenementId]);

  return (
    <div className="fixed inset-0 z-[60] bg-[#0F172A]/70 backdrop-blur-sm grid place-items-center p-3 sm:p-6 fade-in overflow-y-auto" onClick={onClose} data-testid="billetterie-stats-modal">
      <div className="card max-w-3xl w-full my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div>
            <div className="label-uppercase mb-1 flex items-center gap-1.5" style={{ color: 'var(--theme-primary)' }}><BarChart3 className="w-3 h-3" /> Stats billetterie</div>
            <h3 className="font-display text-lg sm:text-xl font-bold text-[#0F172A]">{stats?.nom || 'Chargement…'}</h3>
            {stats?.mode_spectacle && (
              <div className="text-xs text-[#475569] mt-1 inline-flex items-center gap-1"><Theater className="w-3 h-3" /> Mode Spectacle</div>
            )}
          </div>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]" aria-label="Fermer" data-testid="stats-close"><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <div className="py-12 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>
        ) : !stats ? (
          <div className="py-12 text-center text-[#475569]">Aucune donnée.</div>
        ) : (
          <>
            {/* KPI globaux */}
            <div className="grid grid-cols-3 gap-3 mb-5" data-testid="stats-kpis">
              <div className="card !p-3 text-center">
                <div className="label-uppercase mb-1">Capacité</div>
                <div className="font-display text-2xl font-black text-[#0F172A]" data-testid="stat-capacite">{stats.capacite}</div>
              </div>
              <div className="card !p-3 text-center">
                <div className="label-uppercase mb-1">Vendus</div>
                <div className="font-display text-2xl font-black" style={{ color: 'var(--theme-primary)' }} data-testid="stat-vendus">{stats.total_billets}</div>
                {stats.capacite > 0 && <div className="text-[10px] text-[#94A3B8]">{Math.round(stats.total_billets * 100 / stats.capacite)}%</div>}
              </div>
              <div className="card !p-3 text-center">
                <div className="label-uppercase mb-1">Recettes</div>
                <div className="font-display text-2xl font-black text-emerald-600" data-testid="stat-recettes">{stats.recettes}€</div>
              </div>
            </div>

            {/* Zones */}
            <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1" data-testid="stats-zones-list">
              {stats.zones.map((z) => (
                <div key={z.id} className="border border-[#E2E8F0] rounded-lg p-3 sm:p-4 bg-white" style={{ borderLeftWidth: 4, borderLeftColor: COLOR_HEX[z.color] || '#4f8aff' }} data-testid={`stats-zone-${z.id}`}>
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div>
                      <div className="font-bold text-[#0F172A] text-sm">{z.label} {z.standing && <span className="text-xs text-[#475569]">· Debout</span>}</div>
                      <div className="text-xs text-[#475569] mt-0.5">{z.vendus} / {z.capacite} place{z.capacite > 1 ? 's' : ''} vendues · {z.restants} restantes</div>
                    </div>
                    <div className="w-32 h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden">
                      <div className="h-full transition-all" style={{ width: `${z.capacite ? Math.min(100, z.vendus * 100 / z.capacite) : 0}%`, background: COLOR_HEX[z.color] || '#4f8aff' }} />
                    </div>
                  </div>
                  <div className="space-y-1.5" data-testid={`stats-zone-${z.id}-tarifs`}>
                    {z.tarifs.map((t) => (
                      <div key={t.id} className="flex items-center justify-between text-xs gap-2 p-2 bg-[#F9F9F8] rounded">
                        <div className="flex items-center gap-1.5 flex-1 min-w-0">
                          <Tag className="w-3 h-3 text-[#94A3B8] shrink-0" />
                          <span className="font-medium text-[#0F172A] truncate">{t.label}</span>
                          <span className="text-[#475569] shrink-0">· {t.prix}€</span>
                        </div>
                        <div className="text-[#475569] tabular-nums shrink-0">
                          <strong className="text-[#0F172A]">{t.vendus}</strong>
                          {t.quota != null && <span> / {t.quota}</span>}
                          {t.quota != null && t.restants === 0 && <span className="ml-1 text-rose-500">épuisé</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 justify-end mt-4 pt-4 border-t border-[#F1F5F9]">
              <button onClick={() => setShowAttribution(true)} className="btn-secondary flex items-center gap-1.5 text-sm" data-testid="open-attribution">
                <UserPlus className="w-4 h-4" /> Attribuer un billet
              </button>
              <button onClick={onClose} className="btn-primary">Fermer</button>
            </div>
          </>
        )}

        {showAttribution && stats && (
          <AttributionForm
            evenementId={evenementId}
            zones={stats.zones}
            onClose={() => setShowAttribution(false)}
            onSuccess={() => { setShowAttribution(false); load(); }}
          />
        )}
      </div>
    </div>
  );
}

function AttributionForm({ evenementId, zones, onClose, onSuccess }) {
  const [zone, setZone] = useState(zones[0]?.id || '');
  const [tarifId, setTarifId] = useState(zones[0]?.tarifs[0]?.id || '');
  const [siege, setSiege] = useState('');
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const z = zones.find((x) => x.id === zone);
  const t = z?.tarifs.find((x) => x.id === tarifId);

  const submit = async (e) => {
    e.preventDefault();
    if (!email && !nom) { toast.error('Saisissez au moins un nom ou un email'); return; }
    setSaving(true);
    try {
      await api.post('/billets/staff-attribution', {
        evenement_id: evenementId,
        zone,
        tarif_id: tarifId,
        siege: z?.standing ? null : (siege || null),
        email_acheteur: email,
        nom_acheteur: nom,
        statut_paiement: t?.prix === 0 ? 'offert' : 'paye_offline',
      });
      toast.success('Billet attribué');
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-[#0F172A]/80 backdrop-blur-sm grid place-items-center p-3 sm:p-6 fade-in" onClick={onClose} data-testid="attribution-modal">
      <form onSubmit={submit} className="card max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold text-[#0F172A] flex items-center gap-2"><UserPlus className="w-5 h-5" /> Attribuer un billet</h3>
          <button type="button" onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label-uppercase block mb-1.5">Zone</label>
            <select
              value={zone}
              onChange={(e) => { setZone(e.target.value); const newZ = zones.find((x) => x.id === e.target.value); setTarifId(newZ?.tarifs[0]?.id || ''); setSiege(''); }}
              className="input-base"
              data-testid="attr-zone"
            >
              {zones.map((z) => <option key={z.id} value={z.id}>{z.label} {z.standing ? '(debout)' : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="label-uppercase block mb-1.5">Tarif</label>
            <select value={tarifId} onChange={(e) => setTarifId(e.target.value)} className="input-base" data-testid="attr-tarif">
              {(z?.tarifs || []).map((t) => (
                <option key={t.id} value={t.id} disabled={t.restants === 0}>
                  {t.label} — {t.prix}€{t.quota != null ? ` (${t.restants}/${t.quota})` : ''}
                </option>
              ))}
            </select>
          </div>
          {!z?.standing && (
            <div>
              <label className="label-uppercase block mb-1.5">Siège (optionnel)</label>
              <input type="text" value={siege} onChange={(e) => setSiege(e.target.value.toUpperCase())} placeholder="Ex: A-3" className="input-base" data-testid="attr-siege" />
            </div>
          )}
          <div>
            <label className="label-uppercase block mb-1.5">Nom du bénéficiaire</label>
            <input type="text" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Marie Dupont" className="input-base" data-testid="attr-nom" />
          </div>
          <div>
            <label className="label-uppercase block mb-1.5">Email (pour envoi du QR)</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="marie@email.fr" className="input-base" data-testid="attr-email" />
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-4 pt-4 border-t border-[#F1F5F9]">
          <button type="button" onClick={onClose} className="btn-secondary">Annuler</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="attr-submit">{saving ? 'Création…' : 'Créer le billet'}</button>
        </div>
      </form>
    </div>
  );
}
